import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type { PhoneSettingsUpdateInput } from '@cp3/shared';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export function usePhoneSettings() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  const qc = useQueryClient();

  const queryKey = ['phoneSettings', targetUid] as const;

  const query = useQuery({
    queryKey,
    enabled: !!user && !!targetUid,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, { settings: Record<string, unknown> | null }>(
          functions,
          'adminGetPhoneSettings',
        );
        const r = await fn({ uid: targetUid });
        return r.data.settings;
      }
      const fn = httpsCallable<unknown, { settings: Record<string, unknown> | null }>(
        functions,
        'getPhoneSettings',
      );
      const r = await fn({});
      return r.data.settings;
    },
  });

  const mutation = useMutation({
    mutationFn: async (input: PhoneSettingsUpdateInput) => {
      const sanitized = sanitizeForCallable(input);
      if (adminMode && targetUid) {
        const fn = httpsCallable<PhoneSettingsUpdateInput & { uid: string }, { ok: true }>(
          functions,
          'adminUpdatePhoneSettings',
        );
        const r = await fn({ ...sanitized, uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<PhoneSettingsUpdateInput, { ok: true }>(
        functions,
        'updatePhoneSettings',
      );
      const r = await fn(sanitized);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  return { query, mutation };
}

function sanitizeForCallable(input: PhoneSettingsUpdateInput): PhoneSettingsUpdateInput {
  const out = { ...input };
  const sec = out.secrets;
  const hasTwilio = sec?.twilioAuthToken && sec.twilioAuthToken.length > 0;
  const hasTelnyx = sec?.telnyxApiKey && sec.telnyxApiKey.length > 0;
  if (!hasTwilio && !hasTelnyx) {
    delete out.secrets;
  } else {
    out.secrets = {};
    if (hasTwilio) out.secrets.twilioAuthToken = sec!.twilioAuthToken!;
    if (hasTelnyx) out.secrets.telnyxApiKey = sec!.telnyxApiKey!;
  }
  return out;
}
